import { ContractParty } from 'cpfr-common';
import { DefinitionList, DefinitionListItem } from '@cp-shared-19/frontend-ui';
import { Accordion, Hr } from '@vwfs-bronson/bronson-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { emptyObjectOrAllValuesAreUndefined } from '../../../../utils';
import { ErrorNotification } from '../../../notifications/error/ErrorNotification';

type ContractPartiesProps = {
    contractParties?: ContractParty[];
    contractId?: string;
};

const DEALER_ROLE = 'Concessionnaire';

export const ContractPartiesComponent: React.FC<ContractPartiesProps> = ({ contractParties, contractId }) => {
    const { t } = useTranslation('contract-parties');

    if (!contractParties) return null;

    const isDealer = (role?: string): boolean => role === DEALER_ROLE;

    const itemLists: DefinitionListItem[][] = contractParties.map(({ name = '', surname = '', role, address, phone }) =>
        [
            {
                label: t('name'),
                value: name || surname ? `${name} ${surname}`.trim() : undefined,
                testId: 'contract-parties-name',
            },
            {
                label: t('street'),
                value: isDealer(role) ? address?.street : undefined,
                testId: 'contract-parties-street',
            },
            {
                label: t('postal-code'),
                value: isDealer(role) ? address?.postalCode : undefined,
                testId: 'contract-parties-postal-code',
            },
            {
                label: t('city'),
                value: isDealer(role) ? address?.city : undefined,
                testId: 'contract-parties-city',
            },
            {
                label: t('role'),
                value: role,
                testId: 'contract-parties-role',
            },
            {
                label: t('phone'),
                value: phone,
                testId: 'contract-parties-phone',
            },
        ].filter(({ value }) => !!value),
    );

    const isError = contractParties.every((contractParty) => emptyObjectOrAllValuesAreUndefined(contractParty));

    return (
        <Accordion.Item
            title={t('title')}
            titleLevel={4}
            id={`contract-parties-${contractId}`}
            contentClassName={'u-pr-none'}
        >
            {isError ? (
                <ErrorNotification testId={'not-found-error'} headline={t('error.headline')} text={t('error.text')} />
            ) : (
                <div>
                    {itemLists.map((itemList, index) => (
                        <div key={`contract-party-${index}`}>
                            {!!index && <Hr className={'u-mv'} />}
                            <DefinitionList split={true} list={itemList} />
                        </div>
                    ))}
                </div>
            )}
        </Accordion.Item>
    );
};
